import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billing } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, Badge } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, MetricCard } from '@repletoryreach/sdk-ui/data-display';
import { Select, NumberInput, SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, show, updateNotification } from '@repletoryreach/sdk-ui/feedback';
import { formatMinor, formatCount } from '../../../lib/format';
import { useCredits } from '../../../lib/credits';
import { shownBalance } from '../../../lib/wallet';
import { useT } from '../../../i18n/I18nProvider';
import '../billing.css';
import { useWallet } from '../../../data/useWallet';
import { usePaymentProviders, PAYMENT_PROVIDERS_KEY } from '../../../data/usePaymentProviders';
import { HelpButton } from '../../../app/guides/HelpButton';

/** Top-up presets in minor units, per the country bundle. */
const PRESETS: Record<string, number[]> = {
  ZAR: [10000, 25000, 50000, 100000],
  NGN: [500000, 1000000, 2500000, 5000000],
  KES: [100000, 250000, 500000, 1000000]
};

/**
 * Providers per currency.
 *
 * Stripe was listed for all three currencies and was the ONLY option for KES,
 * while having no Checkout Session wiring behind it: a route that cannot
 * complete. On 7 September 2026 the owner ruled it out of the picker until
 * that is built, and made Paystack the provider for all three currencies, so
 * every currency now has at least one route that works.
 *
 * Note for whoever configures this: Paystack settles per country, so a South
 * African Paystack business cannot settle naira or shillings. The platform
 * holds one `paystack` row today, which means one settlement country. Selling
 * in a second currency needs a second Paystack business and a per-currency
 * row, which is in the engineering backlog.
 *
 * Since 21 September 2026 this table is only what the page falls back to when
 * the platform's own list cannot be read. A provider the platform has switched
 * off was still offered from here, and whoever chose it met an error; the page
 * now asks `GET /v1/payments/providers` and shows only what is on.
 */
const PROVIDERS: Record<string, { value: billing.PaymentProvider; label: string; available: boolean }[]> = {
  ZAR: [
    { value: 'payfast', label: 'PayFast', available: true },
    { value: 'paystack', label: 'Paystack', available: true }
  ],
  NGN: [
    { value: 'paystack', label: 'Paystack', available: true }
  ],
  KES: [
    { value: 'paystack', label: 'Paystack', available: true }
  ]
};

/** Minimum and maximum a tenant may add in one go, in minor units. */
const MIN_MINOR = 5000;
const MAX_MINOR = 100_000_00;

export default function BillingTopupPage() {
  const t = useT();
  const credits = useCredits();
  const nav = useNavigate();
  const qc = useQueryClient();

  const walletQ = useWallet();
  const ledgerQ = useQuery({ queryKey: ['ledger', 'recent'], queryFn: () => billing.listLedger({ limit: 20 }) });

  const wallet = walletQ.data?.data;
  // The wallet holds credits (one credit is one rand); the payment can be made
  // in rand, naira or shillings and converts at the platform's rate.
  const [currency, setCurrency] = useState<string>('ZAR');
  const fxQ = useQuery({ queryKey: ['public-pricing'], queryFn: () => billing.getPublicPricing(), staleTime: 60_000 });
  const perZar = useMemo(() => {
    if (currency === 'ZAR') return 1;
    const row = fxQ.data?.data.fx.find((f) => f.currency === currency);
    return row ? Number(row.per_zar) : null;
  }, [fxQ.data, currency]);
  const creditsFor = (paidMinor: number) => (perZar && perZar > 0 ? Math.round(paidMinor / perZar) : 0);
  const presets = PRESETS[currency] ?? PRESETS.ZAR;
  const providersQ = usePaymentProviders();
  // What the platform offers for this currency right now. A provider that is
  // switched off is left out altogether rather than shown greyed: the customer
  // cannot do anything about it, so it is not theirs to read.
  const providers = useMemo(() => {
    if (!providersQ.data) return PROVIDERS[currency] ?? PROVIDERS.ZAR;
    return providersQ.data
      .filter((p) => p.available && p.currencies.includes(currency))
      .map((p) => ({ value: p.provider, label: p.name, available: true }));
  }, [providersQ.data, currency]);
  const usable = providers.filter((p) => p.available);

  const [amount, setAmount] = useState<number>(presets[1]);
  const [provider, setProvider] = useState<billing.PaymentProvider>(usable[0]?.value ?? 'payfast');
  // The list arrives after the first render and can change under the page: keep
  // the choice on something that is actually offered.
  useEffect(() => {
    if (usable.length > 0 && !usable.some((p) => p.value === provider)) setProvider(usable[0].value);
  }, [usable, provider]);
  const [reference, setReference] = useState<string | null>(null);


  useEffect(() => { setAmount(presets[1]); }, [presets]);

  /*
   * §4 item 6: the presets were chosen blind. Derived from what this tenant has
   * actually been charged, so it reflects their own template mix.
   */
  const perMessage = useMemo(() => {
    const debits = (ledgerQ.data?.data ?? []).filter((e) => e.amount_minor < 0 && e.sku_code);
    if (debits.length < 3) return null;
    return debits.reduce((sum, e) => sum + Math.abs(e.amount_minor), 0) / debits.length;
  }, [ledgerQ.data]);

  const topUp = useMutation({
    // §4 item 13: this was hand-rolled async + a `busy` flag, so it was the one
    // mutation in this slice that could not drive a pending→resolved toast.
    mutationFn: () => billing.topUp({
      amount_minor: amount,
      currency,
      provider,
      return_url: `${window.location.origin}/billing?topup=returned`
    }),
    onMutate: () => {
      const id = show({
        type: 'info',
        title: t('billing.topup.topUp'),
        message: t('billing.topup.starting'),
        durationMs: 0
      });
      return { id };
    },
    onSuccess: (res, _v, ctx) => {
      // §4 item 8: the only handle a tenant has on a payment that goes wrong.
      setReference(res.data.provider_reference ?? null);
      qc.invalidateQueries({ queryKey: ['wallet'] });

      const url = res.data.redirect_url;
      if (!url) {
        updateNotification(ctx!.id, {
          type: 'error',
          message: t('billing.topup.noCheckoutUrl'),
          durationMs: 8000
        });
        return;
      }
      updateNotification(ctx!.id, { message: t('billing.topup.redirecting'), durationMs: 4000 });
      window.location.href = url;
    },
    onError: (e, _v, ctx) => {
      // Switched off between the page loading and the button being pressed:
      // read the list again so the choice disappears.
      if ((e as { code?: string } | null)?.code === 'PROVIDER_DISABLED') qc.invalidateQueries({ queryKey: PAYMENT_PROVIDERS_KEY });
      if (ctx?.id) updateNotification(ctx.id, { type: 'error', message: topUpError(e, t), durationMs: 10_000 });
      else notifyError(topUpError(e, t), t('billing.topup.topUp'));
    }
  });

  /** §4 item 14: leaving the app for a gateway deserves a beat. */
  function confirmAndGo() {
    openConfirmModal({
      title: t('billing.topup.confirm.title'),
      children: (
        <Stack gap={8}>
          <Text role="bodySmall">
            {t('billing.topup.confirm.body', {
              amount: formatMinor(amount, currency),
              provider: providers.find((p) => p.value === provider)?.label ?? provider
            })}
          </Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.topup.confirm.hint')}</Text>
        </Stack>
      ),
      labels: { confirm: t('billing.topup.confirm.go'), cancel: t('action.cancel') },
      onConfirm: () => topUp.mutate()
    });
  }

  const tooSmall = amount < MIN_MINOR;
  const tooLarge = amount > MAX_MINOR;

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('dashboard.quickActions.topUp')}
        description={t('billing.topup.description')}
        withDivider={false}
        actions={<HelpButton guide="account" />}
      />

      {/* §4 item 5: the balance was not shown on the page where you decide how
          much to add. */}
      <Group gap="sm" wrap="wrap">
        <MetricCard
          label={t('billing.walletBalance')}
          value={wallet ? credits(shownBalance(wallet.credits_minor, wallet.extra_use), currency) : '–'}
          hint={perMessage ? t('billing.roughlyMessages', { count: formatCount(Math.floor(Math.max(0, wallet?.credits_minor ?? 0) / perMessage)) }) : undefined}
          loading={walletQ.isPending}
        />
        <MetricCard
          label={t('billing.topup.youGet')}
          value={credits(creditsFor(amount))}
          hint={currency === 'ZAR' ? t('billing.creditValue.value') : perZar ? t('billing.topup.rateNote', { rate: perZar, currency }) : undefined}
          loading={fxQ.isPending}
        />
        <MetricCard
          label={t('billing.topup.afterTopUp')}
          value={credits((wallet?.credits_minor ?? 0) + creditsFor(amount), currency)}
          hint={perMessage ? t('billing.roughlyMessages', { count: formatCount(Math.floor(((wallet?.credits_minor ?? 0) + creditsFor(amount)) / perMessage)) }) : undefined}
          loading={walletQ.isPending}
        />
      </Group>

      {usable.length === 0 && (
        <Alert color="error" role="alert" title={t('billing.topup.noProvider.title')}>
          {t('billing.topup.noProvider.body', { currency })}
        </Alert>
      )}

      {reference && (
        <Alert color="info">{t('billing.topup.reference', { reference })}</Alert>
      )}

      <Card withBorder data-guide="topup-form">
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="wrap">
            {/* §4 item 10: this was a hardcoded English title. */}
            <Title order={4}>{t('billing.topup.amount', { currency })}</Title>
            <Group gap="sm" align="center">
              <Text role="bodySmall" fw={600} c="var(--rr-color-text-muted)">{t('billing.topup.payIn')}</Text>
              <SegmentedControl
                size="xs"
                value={currency}
                onChange={(v) => setCurrency(v)}
                data={Object.keys(PRESETS).map((c) => ({ value: c, label: c }))}
              />
            </Group>
          </Group>

          {/* §4 item 11: four separate buttons, each its own tab stop, with no
              `aria-pressed`, for what is a single-select. */}
          <SegmentedControl
            value={String(presets.includes(amount) ? amount : '')}
            onChange={(v) => v && setAmount(Number(v))}
            data={presets.map((p) => ({ value: String(p), label: formatMinor(p, currency) }))}
          />

          {/* §4 item 4: four presets and nothing else, though `amount_minor` is
              a free integer. */}
          <NumberInput
            label={t('billing.topup.custom')}
            description={t('billing.topup.custom.hint', {
              min: formatMinor(MIN_MINOR, currency),
              max: formatMinor(MAX_MINOR, currency)
            })}
            error={tooSmall ? t('billing.topup.tooSmall', { min: formatMinor(MIN_MINOR, currency) })
              : tooLarge ? t('billing.topup.tooLarge', { max: formatMinor(MAX_MINOR, currency) })
              : undefined}
            value={amount / 100}
            onChange={(v) => setAmount(typeof v === 'number' ? Math.round(v * 100) : 0)}
            min={MIN_MINOR / 100}
            max={MAX_MINOR / 100}
          />

          {/* With nothing switched on the notice at the top of the page says so; an empty picker would only add to it. */}
          {usable.length > 0 && (
            <Select
              label={t('billing.topup.provider')}
              value={provider}
              onChange={(e) => setProvider(e.currentTarget.value as billing.PaymentProvider)}
              data={providers.map((p) => ({
                value: p.value,
                label: p.available ? p.label : t('billing.topup.provider.unavailable', { name: p.label }),
                disabled: !p.available
              }))}
            />
          )}

          <Text role="caption" c="var(--rr-color-text-muted)">
            {perMessage
              ? t('billing.topup.buys', { amount: formatMinor(amount, currency), credits: credits(creditsFor(amount)), count: formatCount(Math.floor(creditsFor(amount) / perMessage)) })
              : t('billing.topup.buysCredits', { amount: formatMinor(amount, currency), credits: credits(creditsFor(amount)) })}
          </Text>

          <Group justify="space-between" align="center">
            <Button variant="default" onClick={() => nav('/billing')}>{t('action.back')}</Button>
            <Button
              onClick={confirmAndGo}
              loading={topUp.isPending}
              disabled={tooSmall || tooLarge || usable.length === 0}
            >
              {t('billing.topup.topUp')}
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap={6}>
          <Group gap="xs">
            <Text role="bodySmall" fw={600}>{t('billing.topup.howItWorks')}</Text>
            <Badge size="xs" variant="light">{currency}</Badge>
          </Group>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.topup.howItWorks.body')}</Text>
        </Stack>
      </Card>
    </Stack>
  );
}

/**
 * §4 item 3: `isNotConfigured()` exists for exactly the 503 case and was
 * unused, so a tenant on an account where the operator had not set up the
 * gateway got a raw error string.
 */
function topUpError(e: unknown, t: (k: string, v?: Record<string, string | number>) => string): string {
  if (billing.isNotConfigured(e)) return t('billing.topup.notConfigured');
  const code = (e as { code?: string } | null)?.code;
  if (code === 'PROVIDER_DISABLED') return t('billing.topup.providerDisabled');
  if (code === 'PROVIDER_ERROR') return t('billing.topup.providerError');
  if (code === 'UNKNOWN_PROVIDER') return t('billing.topup.unknownProvider');
  return e instanceof Error ? e.message : t('billing.topup.failed');
}
